import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PrevPage from 'client/components/aria/Pagination/PrevPage/PrevPage';
import NextPage from 'client/components/aria/Pagination/NextPage/NextPage';
import PageButton from 'client/components/aria/Pagination/PageButton/PageButton';

import Icon from '@mdi/react';
import { mdiDotsHorizontal } from '@mdi/js';
import { max, min, range } from 'lodash';

import defaultAriaProps from 'client/utils/constants/ariaPropTypes';

const siblingCount = 2;

const Pagination = (props) => {
    // init
    const { currentPage, totalPages, onChange } = props;

    const minPageRage = () => (currentPage - siblingCount) < 1 ? 1 : currentPage - siblingCount;
    const maxPageRange = () => (currentPage + siblingCount) > totalPages ? totalPages : (currentPage + siblingCount);
    const handleUpdatePageRange = () => range(minPageRage(), (maxPageRange() + 1));

    const [pageRange, setPageRange] = useState(handleUpdatePageRange());

    useEffect(() => {
        setPageRange(handleUpdatePageRange());
    }, [currentPage, totalPages]);

    const handlePrevClick = () => {
        if (onChange) {
            onChange(currentPage - 1);
        }
    };

    const handleNextClick = () => {
        if (onChange) {
            onChange(currentPage + 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        if (onChange) {
            onChange(pageNumber);
        }
    };

    const pageNumbers = pageRange.map(page => {
        return <PageButton
            data-testid={props['data-testid'] ? props['data-testid'] + '-page-' + page : null}
            key={page}
            onPress={() => handlePageClick(page)}
            current={currentPage === page}
        >
            {page}
        </PageButton>;
    });

    // page ellipses
    const pageNextEllipses = () => {
        if (max(pageRange) < totalPages) {
            return <>
                {max(pageRange) < (totalPages - 1) &&
                    <div
                        data-testid={props['data-testid'] ? props['data-testid'] + '-next-ellipse' : null}
                        className="govuk-pagination__item govuk-pagination__item--ellipses"
                    >
                        <Icon path={mdiDotsHorizontal} size={1}/>
                    </div>
                }
                <PageButton onPress={() => handlePageClick(totalPages)}>{totalPages}</PageButton>
            </>;
        }
    };

    const pagePrevEllipses = () => {
        if (min(pageRange) > 1) {
            return <>
                <PageButton onPress={() => handlePageClick(1)}>{1}</PageButton>
                {min(pageRange) > siblingCount &&
                    <div
                        data-testid={props['data-testid'] ? props['data-testid'] + '-next-ellipse' : null}
                        className="govuk-pagination__item govuk-pagination__item--ellipses"
                    >
                        <Icon path={mdiDotsHorizontal} size={1}/>
                    </div>
                }
            </>;
        }
    };

    return (
        totalPages > 1 &&
        <nav
            data-testid={props['data-testid']}
            className="govuk-pagination"
            role="navigation"
            aria-label={props['aria-label']}
        >
            {totalPages > 1 && currentPage !== pageRange[0] &&
                <PrevPage
                    onPress={handlePrevClick}
                    data-testid={props['data-testid'] ? props['data-testid'] + '-prev' : null}
                />
            }

            <div className="govuk-pagination__list">
                {pagePrevEllipses()}
                {pageNumbers}
                {pageNextEllipses()}
            </div>

            {currentPage < totalPages &&
                <NextPage
                    onPress={handleNextClick}
                    data-testid={props['data-testid'] ? props['data-testid'] + '-next' : null}
                />
            }
        </nav>
    );
};

Pagination.propTypes = {
    ...defaultAriaProps,
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    onChange: PropTypes.func
};

Pagination.defaultProps = {
    currentPage: 0,
    totalPages: 0
};

export default Pagination;
